/**
 * Terminal Logger Utility
 * Provides structured logging with different log levels for terminal operations
 */

export enum LogLevel {
  DEBUG = 0,
  INFO = 1,
  WARN = 2,
  ERROR = 3,
  NONE = 4
}

interface LogEntry {
  timestamp: Date;
  level: LogLevel;
  sessionId?: string;
  message: string;
  data?: any;
}

class TerminalLogger {
  private logLevel: LogLevel;
  private logs: LogEntry[] = [];
  private maxLogs = 1000; // Keep last 1000 logs in memory for debug mode
  private isDevelopment = process.env.NODE_ENV === 'development';

  constructor() {
    // Set log level based on environment
    this.logLevel = this.isDevelopment ? LogLevel.DEBUG : LogLevel.WARN;
  }

  private shouldLog(level: LogLevel): boolean {
    return level >= this.logLevel;
  }

  private formatMessage(entry: LogEntry): string {
    const timestamp = entry.timestamp.toISOString();
    const level = LogLevel[entry.level];
    const sessionInfo = entry.sessionId ? ` [${entry.sessionId.slice(0, 8)}]` : '';
    return `[${timestamp}] [${level}]${sessionInfo} ${entry.message}`;
  }

  private log(level: LogLevel, message: string, sessionId?: string, data?: any) {
    const entry: LogEntry = {
      timestamp: new Date(),
      level,
      sessionId,
      message,
      data
    };

    // Store in memory for debug mode
    this.logs.push(entry);
    if (this.logs.length > this.maxLogs) {
      this.logs.shift();
    }

    if (!this.shouldLog(level)) {
      return;
    }

    const formattedMessage = this.formatMessage(entry);
    const consoleData = data ? [formattedMessage, data] : [formattedMessage];

    switch (level) {
      case LogLevel.DEBUG:
        if (this.isDevelopment) {
          console.log(...consoleData);
        }
        break;
      case LogLevel.INFO:
        console.info(...consoleData);
        break;
      case LogLevel.WARN:
        console.warn(...consoleData);
        break;
      case LogLevel.ERROR:
        console.error(...consoleData);
        break;
    }
  }

  debug(message: string, sessionId?: string, data?: any) {
    this.log(LogLevel.DEBUG, message, sessionId, data);
  }

  info(message: string, sessionId?: string, data?: any) {
    this.log(LogLevel.INFO, message, sessionId, data);
  }

  warn(message: string, sessionId?: string, data?: any) {
    this.log(LogLevel.WARN, message, sessionId, data);
  }

  error(message: string, sessionId?: string, data?: any) {
    this.log(LogLevel.ERROR, message, sessionId, data);
  }

  // For terminal-specific events
  connection(event: 'connecting' | 'connected' | 'disconnected' | 'error', sessionId: string, details?: any) {
    const level = event === 'error' ? LogLevel.ERROR : LogLevel.INFO;
    this.log(level, `Terminal ${event}`, sessionId, details);
  }

  websocket(event: 'open' | 'close' | 'error' | 'message', sessionId: string, details?: any) {
    const level = event === 'error' ? LogLevel.ERROR : LogLevel.DEBUG;
    this.log(level, `WebSocket ${event}`, sessionId, details);
  }

  // Set log level dynamically (useful for debug mode)
  setLogLevel(level: LogLevel) {
    this.logLevel = level;
  }

  // Get current log level
  getLogLevel(): LogLevel {
    return this.logLevel;
  }

  // Get logs for debug mode
  getLogs(level?: LogLevel): LogEntry[] {
    if (level !== undefined) {
      return this.logs.filter(log => log.level >= level);
    }
    return [...this.logs];
  }

  // Clear logs
  clearLogs() {
    this.logs = [];
  }

  // Export logs for debugging
  exportLogs(): string {
    return this.logs
      .map(entry => {
        const formatted = this.formatMessage(entry);
        return entry.data 
          ? `${formatted}\n  Data: ${JSON.stringify(entry.data, null, 2)}`
          : formatted;
      })
      .join('\n');
  }
}

// Singleton instance
export const terminalLogger = new TerminalLogger();